import { useState, useRef } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6s48jtsbgrc47fimro0';

export default function InquiryForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    branch: '',
    status: '',
    vehicleInterest: '',
    message: '',
    referredBy: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const requiredFields: Array<{ key: keyof typeof formData; id: string }> = [
      { key: 'name', id: 'mil-name' },
      { key: 'email', id: 'mil-email' },
      { key: 'phone', id: 'mil-phone' },
      { key: 'branch', id: 'mil-branch' },
      { key: 'status', id: 'mil-status' },
    ];
    for (const { key, id } of requiredFields) {
      if (!formData[key].trim()) {
        const el = formRef.current?.querySelector<HTMLElement>(`#${id}`);
        if (el) el.focus();
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        body.append(key, value);
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '', email: '', phone: '', branch: '',
          status: '', vehicleInterest: '', message: '', referredBy: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl p-8 border border-slate-700 shadow-2xl">
      <div className="flex items-center gap-3 mb-6">
        <i className="ri-medal-fill text-3xl text-[#F5C842]"></i>
        <h3 className="text-2xl font-bold text-white">
          Claim Your Dog Tag Discount for First Responders
        </h3>
      </div>

      <form onSubmit={handleSubmit} data-readdy-form id="military-discount-inquiry-form" className="space-y-5" ref={formRef}>
        {/* Name */}
        <div>
          <label htmlFor="mil-name" className="block text-sm font-medium text-slate-300 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="mil-name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!formData.name && submitStatus === 'error'}
            autoComplete="name"
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all"
            placeholder="John Smith"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="mil-email" className="block text-sm font-medium text-slate-300 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="mil-email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!formData.email && submitStatus === 'error'}
            autoComplete="email"
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all"
            placeholder="john@example.com"
          />
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="mil-phone" className="block text-sm font-medium text-slate-300 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="mil-phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!formData.phone && submitStatus === 'error'}
            autoComplete="tel"
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all"
            placeholder="(555) 123-4567"
          />
        </div>

        {/* Branch */}
        <div>
          <label htmlFor="mil-branch" className="block text-sm font-medium text-slate-300 mb-2">
            Branch / Service Type *
          </label>
          <select
            id="mil-branch"
            name="branch"
            value={formData.branch}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!formData.branch && submitStatus === 'error'}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all cursor-pointer"
          >
            <option value="">Select your branch or service</option>
            <optgroup label="Military">
              <option value="Army">Army</option>
              <option value="Navy">Navy</option>
              <option value="Air Force">Air Force</option>
              <option value="Marines">Marines</option>
              <option value="Coast Guard">Coast Guard</option>
              <option value="Space Force">Space Force</option>
            </optgroup>
            <optgroup label="First Responders">
              <option value="Police / Law Enforcement">Police / Law Enforcement</option>
              <option value="Fire Department">Fire Department</option>
              <option value="EMT / Paramedic">EMT / Paramedic</option>
              <option value="911 Dispatcher">911 Dispatcher</option>
            </optgroup>
          </select>
        </div>

        {/* Status */}
        <div>
          <label htmlFor="mil-status" className="block text-sm font-medium text-slate-300 mb-2">
            Service Status *
          </label>
          <select
            id="mil-status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            required
            aria-required="true"
            aria-invalid={!formData.status && submitStatus === 'error'}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all cursor-pointer"
          >
            <option value="">Select your status</option>
            <option value="Active Duty">Active Duty</option>
            <option value="Veteran">Veteran</option>
            <option value="Retired">Retired</option>
            <option value="Reserve">Reserve</option>
            <option value="National Guard">National Guard</option>
            <option value="Active First Responder">Active First Responder</option>
            <option value="Retired First Responder">Retired First Responder</option>
          </select>
        </div>

        {/* Vehicle Interest */}
        <div>
          <label htmlFor="mil-vehicleInterest" className="block text-sm font-medium text-slate-300 mb-2">
            What type of vehicle are you looking for?
          </label>
          <input
            type="text"
            id="mil-vehicleInterest"
            name="vehicleInterest"
            value={formData.vehicleInterest}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all"
            placeholder="e.g., SUV, Truck, Sedan"
          />
        </div>

        {/* Referred By */}
        <div>
          <label htmlFor="mil-referredBy" className="block text-sm font-medium text-slate-300 mb-2">
            Were you referred by a fellow service member? (Optional)
          </label>
          <input
            type="text"
            id="mil-referredBy"
            name="referredBy"
            value={formData.referredBy}
            onChange={handleChange}
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all"
            placeholder="Enter their name or referral code"
          />
        </div>

        {/* Message */}
        <div>
          <label htmlFor="mil-message" className="block text-sm font-medium text-slate-300 mb-2">
            Additional Information
          </label>
          <textarea
            id="mil-message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={4}
            maxLength={500}
            aria-describedby="mil-message-count"
            className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-[#F5C842] focus:border-transparent transition-all resize-none"
            placeholder="Any questions or specific requests?"
          />
          <p id="mil-message-count" className="text-xs text-slate-500 mt-1" aria-live="polite">
            {formData.message.length}/500 characters
          </p>
        </div>

        {/* Submit Status */}
        {submitStatus === 'success' && (
          <div role="alert" aria-live="assertive" className="bg-green-900/30 border border-green-700 rounded-lg p-4">
            <p className="text-green-400 text-sm font-medium">
              ✓ Thank you for your service! We'll be in touch within 24 hours.
            </p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div role="alert" aria-live="assertive" className="bg-red-900/30 border border-red-700 rounded-lg p-4">
            <p className="text-red-400 text-sm font-medium">
              Something went wrong. Please try again or call us directly.
            </p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-[#F5C842] to-[#E5B832] text-slate-900 font-bold py-4 px-6 rounded-lg hover:from-[#E5B832] hover:to-[#D5A822] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F5C842] focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting...
            </span>
          ) : (
            'Claim Your Discount'
          )}
        </button>

        <p className="text-xs text-slate-400 text-center mt-4">
          By submitting, you agree to our{' '}
          <a href="/privacy-policy" className="text-[#F5C842] hover:underline">
            Privacy Policy
          </a>
          . We'll verify your military status and apply your discount.
        </p>
      </form>
    </div>
  );
}
